import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Brain, Loader2, Users, Zap } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

export const XpBar = () => {
  const { user } = usePlyxAuth();
  const [xp, setXp] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/plyx/me/xp").then((r) => setXp(r.data)).catch(() => {});
  }, [user]);
  if (!xp) return null;
  return (
    <div className="rounded-2xl bg-white/[0.05] border border-white/10 p-3 mb-5" data-testid="plyx-xp-bar">
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="font-extrabold flex items-center gap-1.5">
          <Zap className="w-4 h-4 text-[#FFB42D]" /> Level {xp.level}
        </span>
        <span className="text-xs text-white/50">{xp.xp} / {xp.next_level_xp} XP</span>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] transition-all"
          style={{ width: `${Math.max(4, xp.progress * 100)}%` }} />
      </div>
      <div className="text-[10px] text-white/40 mt-1">One XP level across every Plyx game — earn it playing, winning and logging in daily.</div>
    </div>
  );
};

const RoomRow = ({ r }) => (
  <Link to={`/plyx/clash/room/${r.code}`} data-testid={`clash-room-${r.code}`}
    className="block rounded-xl bg-white/[0.05] border border-white/10 p-3 hover:border-[#00E0C7]/50 transition-colors">
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="font-bold text-sm">Room {r.code} · {r.category_label}</div>
        <div className="text-xs text-white/50">
          {r.status === "lobby" ? `${r.player_count}/${r.max_players} players — open, tap to join` :
            r.status === "live" ? "LIVE now" : "Finished"}
        </div>
      </div>
      <Users className="w-4 h-4 text-[#00E0C7] shrink-0" />
    </div>
  </Link>
);

export default function PlyxBrainClash() {
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [] });
  const [category, setCategory] = useState("general");
  const [rooms, setRooms] = useState({ open: [], recent: [] });
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  useEffect(() => {
    api.get("/plyx/millions/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  const loadRooms = useCallback(() => {
    if (!user) return;
    api.get("/plyx/clash/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => {
    loadRooms();
    const t = setInterval(loadRooms, 10000);
    return () => clearInterval(t);
  }, [loadRooms]);

  const create = async () => {
    if (!user) { navigate("/plyx/login"); return; }
    setCreating(true);
    try {
      const r = await api.post("/plyx/clash/room", { category });
      toast.success("Room ready — share the code with friends!");
      navigate(`/plyx/clash/room/${r.data.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the room");
    } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen millions-stage text-white pb-24" data-testid="clash-hub">
      <div className="max-w-xl mx-auto px-4 pt-6">
        <Link to="/plyx/games" className="flex items-center gap-1 text-white/60 text-sm mb-4" data-testid="clash-back-games">
          <ArrowLeft className="w-4 h-4" /> All games
        </Link>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Brain className="w-7 h-7 text-[#00E0C7]" /> Plyx Brain Clash
        </h1>
        <p className="text-sm text-white/60 mt-1 mb-5">
          Live quiz showdowns — everyone answers the SAME 8 questions at the same time.
          Speed earns bonus points, streaks multiply your score, 3 lives keep it spicy. Up to 10 players per room.
        </p>

        <XpBar />

        <div className="text-sm font-bold mb-2">Pick a category</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {(meta.categories || []).map((c) => (
            <button key={c.code} type="button" onClick={() => setCategory(c.code)}
              data-testid={`clash-cat-${c.code}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${category === c.code ? "bg-[#00E0C7] text-black" : "bg-white/10"}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={create} disabled={creating} data-testid="clash-create-room"
          className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#00E0C7] to-[#1a7bff] text-black font-extrabold disabled:opacity-60 mb-3">
          {creating ? (<span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building fresh questions…</span>) : "🎤 Host a live quiz room"}
        </button>

        <div className="flex gap-2 mb-6">
          <input value={joinCode} maxLength={6}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="Have a code? e.g. K7PQ2X" data-testid="clash-join-code"
            className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-white/[0.06] border border-white/10 text-sm outline-none focus:border-[#00E0C7] uppercase tracking-widest" />
          <button type="button" disabled={joinCode.length < 4} data-testid="clash-join-btn"
            onClick={() => navigate(`/plyx/clash/room/${joinCode}`)}
            className="px-5 py-2.5 rounded-full bg-white/10 text-sm font-extrabold disabled:opacity-40">Join</button>
        </div>

        {rooms.open.length > 0 && (
          <div className="mb-6" data-testid="clash-open-rooms">
            <div className="text-sm font-bold mb-2">Open rooms — jump in</div>
            <div className="space-y-2">{rooms.open.map((r) => <RoomRow key={r.code} r={r} />)}</div>
          </div>
        )}
        {rooms.recent.length > 0 && (
          <div className="mb-6" data-testid="clash-recent-rooms">
            <div className="text-sm font-bold mb-2">Your recent games</div>
            <div className="space-y-2">{rooms.recent.map((r) => <RoomRow key={r.code} r={r} />)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
